import type {
  WorkbookDefinition,
  WorkbookOutputKind,
  WorkbookSheetKind,
} from '@erp/shared-types';
import { sheetKindOf } from '@erp/workbooks';

/**
 * What an Item's icon depicts. Files, file Sources and File Outputs are all
 * xlsx; a Data Mapping Source is `mapping`, every other Source `data`; a pivot Worksheet is `pivot`; a Report
 * Output is `report`; a Data sink is `sink`; an App Output is `app`.
 */
export type WorkbookItemIconKind =
  | 'xlsx'
  | 'data'
  | 'mapping'
  | 'worksheet'
  | 'pivot'
  | 'freeform'
  | 'report'
  | 'sink'
  | 'app';

/** Each Sheet kind's icon. */
export const SHEET_KIND_ICONS: Record<WorkbookSheetKind, WorkbookItemIconKind> =
  { columnar: 'worksheet', pivot: 'pivot', freeform: 'freeform' };

/** Each Output kind's icon. */
const OUTPUT_ICONS: Record<WorkbookOutputKind, WorkbookItemIconKind> = {
  file: 'xlsx',
  report: 'report',
  sink: 'sink',
  app: 'app',
};

/** The icon kind of the Item `id` names, or undefined when it names none. */
export function workbookItemIconKind(
  definition: Pick<WorkbookDefinition, 'files' | 'sources' | 'sheets'> & {
    /** Absent where only tables are named, as in a Formula. */
    outputs?: WorkbookDefinition['outputs'];
  },
  id: string,
): WorkbookItemIconKind | undefined {
  const output = definition.outputs?.find((item) => item.id === id);
  if (output) return OUTPUT_ICONS[output.kind];
  if (definition.files.some((file) => file.id === id)) return 'xlsx';
  const source = definition.sources.find((item) => item.id === id);
  if (source)
    return source.kind === 'file'
      ? 'xlsx'
      : source.kind === 'mapping'
        ? 'mapping'
        : 'data';
  const sheet = definition.sheets.find((item) => item.id === id);
  if (sheet) return SHEET_KIND_ICONS[sheetKindOf(sheet)];
  return undefined;
}
